import React, {
  useCallback,
  useReducer,
  useRef,
  useState
} from "react";
import * as exsurge from "react-exsurge";
import ChantDocument from "../js/ChantDocument";
import usePersistentState from "../js/usePersistentState";
import "../scss/App.scss";
import "../scss/bootstrap-custom.scss";
import EditorPane from "./EditorPane";
import NavigationHeader from "./NavigationHeader";
import DocumentPanel from "./panels/DocumentPanel";
import FileManagementPanel from "./panels/FileManagementPanel";
import NotePanel from "./panels/NotePanel";
import TextPanel from "./panels/TextPanel";

export const defaultTitleAlignment = "center";

function zoomReducer(zoom, newZoom) {
  if (newZoom < 0.33 || newZoom > 8.92) return zoom;
  return newZoom;
}

function getDefaultChantDocument() {
  const gabc = decodeURIComponent(window.location.hash.slice(1));
  return ChantDocument.getDefaultChantDocument(gabc || undefined);
}

function App() {
  const showFileManagementPanel = false;
  const user = {
      initials: "AB",
      name: "Adam B.",
      email: "adam@sourceandsummit.com"
    },
    navStatus = {}; //{ icon: faSave, message: "Saving..." };
  const docHistoryRef = useRef([]);

  const [doc, setDoc] = useState(getDefaultChantDocument),
    [textTypes, setTextTypes] = useState([]),
    [zoomLevel, setZoomLevel] = useReducer(zoomReducer, 1),
    handleDocumentChange = useCallback(
      newValues => {
        setDoc(doc => {
          let newDoc = { ...doc, ...newValues };
          if (newValues.gabc) {
            const header = new exsurge.GabcHeader(newValues.gabc);
            if ('name' in header) {
              newDoc.name = header.name;
            }
            if ('dpi' in header) {
              const dpi = Number(header.dpi);
              if (dpi) {
                newDoc.dpi = dpi;
              } else {
                delete newDoc.dpi;
              }
            }
          }
          if (newDoc.path) {
            localStorage["file" + newDoc.path] = JSON.stringify(newDoc);
          }
          if (newDoc.units !== doc.units) {
            const oldPixelMultiplier = ChantDocument.getPixelMultiplier(doc.units);
            const pixelMultiplier = ChantDocument.getPixelMultiplier(newDoc.units);
            // update width, height, and margin, if pixel multiplier changed
            if (oldPixelMultiplier !== pixelMultiplier) {
              const defaultsForOld = ChantDocument.getDefaultChantDocumentSize(doc.units);
              if (defaultsForOld.width === doc.width && defaultsForOld.height === doc.height && defaultsForOld.margin === doc.margin) {
                Object.assign(newDoc, ChantDocument.getDefaultChantDocumentSize(newDoc.units));
              } else {
                const width = newDoc.width * oldPixelMultiplier;
                const height = newDoc.height * oldPixelMultiplier;
                const margin = newDoc.margin * oldPixelMultiplier;
                newDoc.width = Math.round(100 * width / pixelMultiplier) / 100;
                newDoc.height = Math.round(100 * height / pixelMultiplier) / 100;
                newDoc.margin = Math.round(100 * margin / pixelMultiplier) / 100;
              }
            }
            localStorage.units = newDoc.units;
          }
          let history = docHistoryRef.current;
          if (typeof history.index === "number")
            history.splice(history.index, history.length - history.index);
          history.index = history.push(newDoc);
          return newDoc;
        });
      },
      [setDoc]
    ),
    showFile = useCallback(
      file => {
        let fileContents = localStorage["file" + file.path + file.name];
        if (fileContents) {
          fileContents = JSON.parse(fileContents);
        } else {
          fileContents = ChantDocument.getDefaultChantDocument();
        }
        fileContents.name = file.name;
        let filePath = (fileContents.path = file.path + file.name);
        // reset undo history:
        let history = (docHistoryRef.current = []);
        history.index = history.push(fileContents);
        setDoc(fileContents);
        return filePath;
      },
      [setDoc]
    ),
    [selectedFilePath, setSelectedFilePath] = usePersistentState(
      "selectedFilePath",
      selectedFilePath => {
        let match = /^(\/.*?)\/?([^/]+)$/.exec(selectedFilePath);
        if (match) {
          showFile({ path: match[1] || "", name: match[2] });
        }
        return selectedFilePath;
      }
    ),
    scoreRef = useRef(),
    headerLengthRef = useRef(0),
    selectionStateReducer = useCallback(
      (selection, newSelection = selection) => {
        const score = scoreRef.current,
          { notePanelProps, textPanelProps } = newSelection;
        if (notePanelProps) {
          if (notePanelProps.noteIndices && notePanelProps.noteIndices.length) {
            let selection = notePanelProps.noteIndices
              .map(i => score.notes[i])
              .filter(
                elem =>
                  elem instanceof exsurge.Note ||
                  elem instanceof exsurge.Accidental ||
                  elem instanceof exsurge.Custos
              );
            newSelection = {
              ...newSelection,
              notePanelProps: { ...notePanelProps, selection }
            };
          }
        } else if (textPanelProps) {
          if (
            textPanelProps.selection &&
            textPanelProps.selection.length &&
            textPanelProps.selection.every(textElem => !!textElem.source)
          ) {
            let selection = textPanelProps.selection.map(textElem => ({
              ...textElem,
              source: textElem.source.getFromScore(score)
            }));
            newSelection = {
              ...newSelection,
              textPanelProps: { ...textPanelProps, selection }
            };
          }
        } else {
          const noteSelection = newSelection.element,
            textSelection = newSelection.text;
          if (
            noteSelection &&
            noteSelection.indices &&
            noteSelection.indices.length
          ) {
            const selectedNotes = noteSelection.indices
              .map(i => score.notes[i])
              .filter(
                elem =>
                  elem instanceof exsurge.Note ||
                  elem instanceof exsurge.Accidental ||
                  elem instanceof exsurge.Custos
              );
            newSelection.notePanelProps = {
              selection: selectedNotes,
              noteIndices: noteSelection.indices
            };
          } else {
            newSelection.notePanelProps = null;
          }
          if (textSelection && textSelection.length) {
            newSelection.textPanelProps = { selection: textSelection };
          } else {
            newSelection.textPanelProps = null;
          }
        }
        return newSelection;
      },
      []
    ),
    [exsurgeSelection, setExsurgeSelection] = useReducer(
      selectionStateReducer,
      {}
    ),
    undoDocumentChange = useCallback(
      (offset = -1) => {
        let history = docHistoryRef.current;
        let index = (history.index || history.length) + offset;
        let document = history[index - 1];
        console.info("attempting undo", offset, index, history);
        if (document) {
          console.info("success");
          history.index = index;
          setDoc(document);
          setExsurgeSelection({});
        }
      },
      [setDoc, setExsurgeSelection]
    ),
    handleSelectedFile = useCallback(
      newFile => {
        let filePath = showFile(newFile);
        setSelectedFilePath(filePath);
        setExsurgeSelection({});
      },
      [showFile, setSelectedFilePath, setExsurgeSelection]
    ),
    updateScore = useCallback((score, headerLength) => {
      scoreRef.current = score;
      headerLengthRef.current = headerLength;
      let newTextTypes = [];
      for (let [key, textType] of Object.entries(exsurge.TextTypes)) {
        if (textType.containedInScore(score)) {
          newTextTypes.push(key);
        }
      }
      setTextTypes(textTypes =>
        newTextTypes.join(",") === textTypes.join(",")
          ? textTypes
          : newTextTypes
      );
      setExsurgeSelection();
    }, []);

  const updateSelectionGabc = useCallback(
    (selection, newGabc, insertion = false, options = {}) => {
      let headerLen = headerLengthRef.current,
        gabc = doc.gabc;
      let singleNewGabc = typeof newGabc === "string",
        newGabcString = singleNewGabc && newGabc;
      if (singleNewGabc) newGabc = new Array(selection.length).fill("");
      let newSelection = null;
      selection = selection
        .map((note, i) => ({ note, newGabc: newGabc[i] }))
        .sort((a, b) => b.note.sourceIndex - a.note.sourceIndex);
      if (singleNewGabc) {
        let elementIndex;
        if (insertion) {
          let gabcIndex;
          if (insertion.inLyricOfElementIndex >= 0) {
            let note = scoreRef.current.notes[insertion.inLyricOfElementIndex];
            gabcIndex = note.lyrics?.[0]?.sourceIndex ?? 0;
            if (options.lyricOffset) {
              gabcIndex += options.lyricOffset;
              const oldGabc = gabc.slice(headerLen + gabcIndex);
              const parenMatch = /^\s*\(/.exec(oldGabc);
              if (parenMatch) {
                gabcIndex += parenMatch[0].length;
              } else {
                newGabcString = `(${newGabcString})`;
              }
            }
          } else if (insertion.afterElementIndex >= 0) {
            let note = scoreRef.current.notes[insertion.afterElementIndex];
            gabcIndex = note.sourceIndex + note.sourceGabc.length;
            elementIndex = insertion.afterElementIndex;
            // only increment the element index if it wasn't replacing a TextOnly
            if (note.sourceGabc.length) ++elementIndex;
          } else {
            // insertion at the beginning of the score.
            elementIndex = 0;
            if (scoreRef.current.notes.length) {
              gabcIndex = scoreRef.current.notes[0].sourceIndex;
            } else {
              gabcIndex = gabc.length;
              newGabcString = `(${newGabcString})`;
            }
          }
          gabc =
            gabc.slice(0, headerLen + gabcIndex) +
            newGabcString +
            gabc.slice(headerLen + gabcIndex);
          selection = [];
        } else {
          let earliestSelection = selection[selection.length - 1];
          earliestSelection.newGabc = newGabcString;
          elementIndex = earliestSelection.note.elementIndex;
          if (newGabcString === "") --elementIndex;
        }
        newSelection = {};
        if (elementIndex >= 0 && (newGabcString || options.keepSelection)) {
          newSelection = {
            element: {
              indices: [elementIndex],
              insertion: { afterElementIndex: elementIndex }
            }
          };
        }
      }
      let header = new exsurge.GabcHeader(gabc);
      gabc = gabc.slice(headerLen);
      selection.forEach(({ note, newGabc }) => {
        if (note instanceof exsurge.TitleTextElement || note instanceof exsurge.Annotation) {
          // just need to update the header:
          let key = note.headerKey || note.textType.cssClass || note.textType.key,
              arrayKey = key + 'Array',
            oldGabc = header[key],
            oldGabcArray = header[arrayKey];
          if (oldGabcArray) {
            const index = oldGabcArray.indexOf(note.sourceGabc);
            if (index < 0) {
              console.warn("unmatched GABC:", oldGabcArray, note.sourceGabc);
            } else {
              oldGabcArray[index] = newGabc;
            }
          } else {
            if (oldGabc !== note.sourceGabc)
              console.warn("unmatched GABC:", oldGabc, note.sourceGabc);
            header[key] = newGabc;
          }
        } else {
          let oldGabc = gabc.substr(
            note.sourceIndex || 0,
            note.sourceGabc.length || 0
          );
          if (oldGabc && note.sourceGabc && oldGabc !== note.sourceGabc)
            console.warn("unmatched GABC:", oldGabc, note.sourceGabc);
          let prefix = gabc.slice(0, note.sourceIndex),
            suffix = gabc.slice(note.sourceIndex + oldGabc.length),
            neume = note.neume || note;
          if (!neume) return;
          if (
            neume.hasLyrics &&
            !neume.hasLyrics() &&
            !neume.translationText &&
            !neume.alText &&
            newGabc === "" &&
            prefix.slice(-1) === "(" &&
            suffix[0] === ")"
          ) {
            // we remove also the empty parentheses when there are no lyrics attached to them
            prefix = prefix.slice(0, prefix.length - 1);
            suffix = suffix.slice(1);
          }
          if (
            suffix[0] === "(" &&
            note instanceof exsurge.Lyric &&
            newGabc.slice(-2) === "()"
          ) {
            newGabc = newGabc.slice(0, -2);
          }
          gabc = prefix + newGabc + suffix;
        }
      });
      gabc = header.toString() + gabc;
      handleDocumentChange({ gabc });
      if (newSelection !== null) {
        setExsurgeSelection(newSelection);
      }
    },
    [doc.gabc, setExsurgeSelection, handleDocumentChange]
  );
  const { notePanelProps, textPanelProps } = exsurgeSelection;
  let rightPanel =
    notePanelProps && notePanelProps.selection.length ? (
      <NotePanel onSetShape={updateSelectionGabc} {...notePanelProps} />
    ) : textPanelProps ? (
      <TextPanel
        onUpdateText={updateSelectionGabc}
        {...textPanelProps}
        onUpdateExsurgeSelection={setExsurgeSelection}
        document={doc}
        onChange={handleDocumentChange}
      />
    ) : (
      <DocumentPanel
        document={doc}
        textTypes={textTypes}
        onChange={handleDocumentChange}
      />
    );
  const handleChangeFilename = useCallback(
    name => handleDocumentChange({ name }),
    [handleDocumentChange]
  );
  const handleChangeGabc = useCallback(gabc => handleDocumentChange({ gabc }), [
    handleDocumentChange
  ]);

  // const totalState = {
  //   doc,
  //   textTypes,
  //   zoomLevel,
  //   exsurgeSelection
  // };
  // const previous = usePrevious(totalState) || {};
  // const changedState = Object.entries(totalState).reduce(
  //   (result, [key, value]) => {
  //     if (value !== previous[key]) result[key] = [value, previous[key]];
  //     return result;
  //   },
  //   {}
  // );

  // console.info(changedState);

  return (
    <div className="App d-flex flex-column">
      <NavigationHeader
        user={user}
        document={doc}
        statusDisplay={navStatus}
        onLogOut={() => console.info("logged out")}
        zoom={zoomLevel}
        onZoom={setZoomLevel}
      />
      <div className="App-body flex-fill d-flex p-relative">
        {showFileManagementPanel ? (
          <FileManagementPanel
            onSelectFile={handleSelectedFile}
            selectedFilePath={selectedFilePath}
          />
        ) : null}
        <EditorPane
          document={doc}
          zoom={zoomLevel}
          exsurgeSelection={exsurgeSelection}
          onUndoDocumentChange={undoDocumentChange}
          onChangeFilename={handleChangeFilename}
          onChange={handleChangeGabc}
          onScoreUpdate={updateScore}
          onUpdateNoteGabc={updateSelectionGabc}
          onUpdateExsurgeSelection={setExsurgeSelection}
        />
        {rightPanel}
      </div>
    </div>
  );
}

export default App;
